import React from "react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Edit3, Trash2, ChevronDown, ChevronRight, Layers, User, Video } from "lucide-react";
import { toast } from "sonner";

function ruleLine(rule) {
    if (!rule) return null;
    if (rule.payment_type === "rev_share") return `${rule.rev_share_pct}% rev-share`;
    return `$${rule.flat_amount} flat`;
}

function RuleEditor({ open, onClose, title, subtitle, initial, onSave }) {
    const [paymentType, setPaymentType] = React.useState(initial?.payment_type || "rev_share");
    const [rev, setRev] = React.useState(initial?.rev_share_pct ?? 10);
    const [flat, setFlat] = React.useState(initial?.flat_amount ?? 100);

    React.useEffect(() => {
        setPaymentType(initial?.payment_type || "rev_share");
        setRev(initial?.rev_share_pct ?? 10);
        setFlat(initial?.flat_amount ?? 100);
    }, [initial, open]);

    const submit = async () => {
        const body = { payment_type: paymentType };
        if (paymentType === "rev_share") body.rev_share_pct = parseFloat(rev);
        else body.flat_amount = parseFloat(flat);
        try {
            await onSave(body);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="bg-white border-slate-200 rounded-2xl max-w-md"
                data-testid="rule-editor"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
                    {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
                </DialogHeader>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                        {[
                            { v: "rev_share", label: "Rev share %" },
                            { v: "flat", label: "Flat fee $" },
                        ].map((o) => (
                            <button
                                key={o.v}
                                onClick={() => setPaymentType(o.v)}
                                className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                                    paymentType === o.v
                                        ? "border-coral bg-coral/5 text-slate-900"
                                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                                }`}
                                data-testid={`payment-type-${o.v}`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                    {paymentType === "rev_share" ? (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Revenue share (%)</Label>
                            <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="100"
                                value={rev}
                                onChange={(e) => setRev(e.target.value)}
                                className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                                data-testid="rev-share-input"
                            />
                            <div className="text-xs text-slate-500">
                                Creator earns this % of revenue Castline attributes back to this video.
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Flat fee (USD)</Label>
                            <Input
                                type="number"
                                step="10"
                                min="0"
                                value={flat}
                                onChange={(e) => setFlat(e.target.value)}
                                className="rounded-xl border-slate-200 h-11 focus-visible:ring-coral focus-visible:ring-2"
                                data-testid="flat-amount-input"
                            />
                            <div className="text-xs text-slate-500">
                                One-time payout to the creator regardless of performance.
                            </div>
                        </div>
                    )}
                    <button
                        onClick={submit}
                        className="btn-pill-primary w-full"
                        data-testid="rule-save"
                    >
                        Save terms
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function BrandPayoutTerms() {
    const [rules, setRules] = React.useState({ default: null, by_creator: [], by_video: [] });
    const [creators, setCreators] = React.useState([]);
    const [videosByCreator, setVideosByCreator] = React.useState({});
    const [expanded, setExpanded] = React.useState(null);
    const [editor, setEditor] = React.useState(null); // {scope,target,initial,title,subtitle}

    const load = async () => {
        const [r, c, v] = await Promise.all([
            api.get("/brand/payout-rules"),
            api.get("/brand/creators"),
            api.get("/brand/videos"),
        ]);
        setRules(r.data);
        setCreators(c.data);
        const grouped = {};
        for (const vid of v.data) {
            if (!grouped[vid.creator_id]) grouped[vid.creator_id] = [];
            grouped[vid.creator_id].push(vid);
        }
        setVideosByCreator(grouped);
    };

    React.useEffect(() => {
        load();
    }, []);

    const creatorRule = (cid) => rules.by_creator.find((r) => r.target_id === cid);
    const videoRule = (vid) => rules.by_video.find((r) => r.target_id === vid);

    const effectiveForCreator = (cid) => creatorRule(cid) || rules.default;
    const effectiveForVideo = (vid, cid) =>
        videoRule(vid) || creatorRule(cid) || rules.default;

    const saveDefault = async (body) => {
        await api.put("/brand/payout-rules/default", body);
        toast.success("Default payout terms updated");
        load();
    };
    const saveCreator = async (cid, body) => {
        await api.put(`/brand/payout-rules/creator/${cid}`, body);
        toast.success("Creator override saved");
        load();
    };
    const saveVideo = async (vid, body) => {
        await api.put(`/brand/payout-rules/video/${vid}`, body);
        toast.success("Video override saved");
        load();
    };
    const deleteRule = async (id) => {
        if (!window.confirm("Remove this override? It will fall back to the parent terms.")) return;
        await api.delete(`/brand/payout-rules/${id}`);
        toast.success("Override removed");
        load();
    };

    const cancelPending = async (rule) => {
        if (!window.confirm("Withdraw the pending decrease? Current terms remain in place.")) return;
        await api.post(`/brand/payout-rules/${rule.id}/cancel-pending`);
        toast.success("Pending change withdrawn");
        load();
    };

    const renderPending = (rule) => {
        if (!rule?.pending) return null;
        const acc = rule.pending.acceptances || {};
        const total = Object.keys(acc).length;
        const accepted = Object.values(acc).filter((s) => s === "accepted").length;
        return (
            <div
                className="mt-3 surface-soft rounded-xl p-3 text-xs flex items-center justify-between gap-2 flex-wrap"
                data-testid={`pending-${rule.id}`}
            >
                <div className="text-slate-600">
                    <span className="font-semibold text-coral">Pending decrease →</span>{" "}
                    {rule.pending.payment_type === "rev_share"
                        ? `${rule.pending.rev_share_pct}% rev-share`
                        : `$${rule.pending.flat_amount} flat`}
                    <span className="text-slate-400">
                        {" "}· {accepted}/{total} creators accepted
                    </span>
                </div>
                <button
                    onClick={() => cancelPending(rule)}
                    className="btn-pill-ghost text-xs px-3 py-1.5"
                    data-testid={`cancel-pending-${rule.id}`}
                >
                    Withdraw
                </button>
            </div>
        );
    };

    return (
        <div className="space-y-5" data-testid="brand-payout-terms">
            {/* DEFAULT */}
            <div className="card-ecom p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Layers className="w-4 h-4 text-coral" />
                            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                                Bulk default
                            </span>
                        </div>
                        <div className="font-display text-xl font-semibold">
                            {rules.default
                                ? ruleLine(rules.default)
                                : "No default set yet"}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Applies to every video from every connected creator unless overridden.
                        </div>
                    </div>
                    <button
                        onClick={() =>
                            setEditor({
                                title: rules.default ? "Edit bulk default" : "Set bulk default",
                                subtitle: "Default payout for all your creators.",
                                initial: rules.default,
                                onSave: saveDefault,
                            })
                        }
                        className="btn-pill-primary inline-flex items-center text-sm"
                        data-testid="edit-default-rule"
                    >
                        <Edit3 className="w-4 h-4 mr-1.5" />
                        {rules.default ? "Edit default" : "Set default"}
                    </button>
                </div>
                {renderPending(rules.default)}
            </div>

            {/* PER-CREATOR */}
            <div className="card-ecom">
                <div className="p-5 border-b hairline">
                    <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-coral" />
                        <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
                            Per-creator overrides
                        </span>
                    </div>
                    <div className="text-sm text-slate-500">
                        Override the default for an individual creator. Per-video overrides still beat this.
                    </div>
                </div>
                {creators.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500" data-testid="no-creators-payout">
                        No creators connected yet.
                    </div>
                ) : (
                    <div className="divide-y hairline" data-testid="creators-payout-list">
                        {creators.map((c) => {
                            const cRule = creatorRule(c.id);
                            const eff = effectiveForCreator(c.id);
                            const isOpen = expanded === c.id;
                            const vids = videosByCreator[c.id] || [];
                            return (
                                <div key={c.id} data-testid={`creator-payout-${c.id}`}>
                                    <div className="p-4 flex items-center gap-3 flex-wrap">
                                        <button
                                            onClick={() =>
                                                setExpanded(isOpen ? null : c.id)
                                            }
                                            className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                            data-testid={`expand-creator-${c.id}`}
                                        >
                                            {isOpen ? (
                                                <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                            ) : (
                                                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                            )}
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold flex-shrink-0">
                                                {c.name?.[0]?.toUpperCase()}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-display font-semibold truncate">
                                                    {c.name}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate">
                                                    {vids.length} videos · effective {ruleLine(eff) || "no terms"}
                                                </div>
                                            </div>
                                        </button>
                                        <div className="flex gap-2">
                                            {cRule ? (
                                                <>
                                                    <span className="chip chip-lime text-xs" data-testid={`creator-rule-${c.id}`}>
                                                        Override · {ruleLine(cRule)}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            setEditor({
                                                                title: `Edit override for ${c.name}`,
                                                                initial: cRule,
                                                                onSave: (b) => saveCreator(c.id, b),
                                                            })
                                                        }
                                                        className="btn-pill-ghost text-xs px-3 py-1.5 inline-flex items-center"
                                                        data-testid={`edit-creator-rule-${c.id}`}
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteRule(cRule.id)}
                                                        className="btn-pill-ghost text-xs px-3 py-1.5 inline-flex items-center"
                                                        data-testid={`remove-creator-rule-${c.id}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        setEditor({
                                                            title: `Override for ${c.name}`,
                                                            subtitle: "Different terms for this creator only.",
                                                            initial: null,
                                                            onSave: (b) => saveCreator(c.id, b),
                                                        })
                                                    }
                                                    className="btn-pill-ghost text-xs px-3 py-1.5 inline-flex items-center"
                                                    data-testid={`add-creator-rule-${c.id}`}
                                                >
                                                    <Edit3 className="w-3.5 h-3.5 mr-1" /> Override
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {cRule && renderPending(cRule) && (
                                        <div className="px-4 pb-2 pl-12">
                                            {renderPending(cRule)}
                                        </div>
                                    )}
                                    {isOpen && (
                                        <div className="px-4 pb-4 pl-12">
                                            {vids.length === 0 ? (
                                                <div className="text-xs text-slate-400 py-3">No videos yet.</div>
                                            ) : (
                                                <div className="surface-soft rounded-xl divide-y hairline">
                                                    {vids.map((v) => {
                                                        const vRule = videoRule(v.id);
                                                        const e = effectiveForVideo(v.id, c.id);
                                                        return (
                                                            <div
                                                                key={v.id}
                                                                className="p-3 flex items-center gap-3 flex-wrap"
                                                                data-testid={`video-payout-${v.id}`}
                                                            >
                                                                <Video className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm truncate">
                                                                        {v.title}
                                                                    </div>
                                                                    <div className="text-[11px] text-slate-500">
                                                                        effective {ruleLine(e) || "no terms"}
                                                                    </div>
                                                                </div>
                                                                {vRule ? (
                                                                    <>
                                                                        <span className="chip chip-coral text-[11px]">
                                                                            Video override · {ruleLine(vRule)}
                                                                        </span>
                                                                        <button
                                                                            onClick={() =>
                                                                                setEditor({
                                                                                    title: `Edit ${v.title}`,
                                                                                    initial: vRule,
                                                                                    onSave: (b) =>
                                                                                        saveVideo(v.id, b),
                                                                                })
                                                                            }
                                                                            className="btn-pill-ghost text-xs px-2 py-1"
                                                                            data-testid={`edit-video-rule-${v.id}`}
                                                                        >
                                                                            <Edit3 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => deleteRule(vRule.id)}
                                                                            className="btn-pill-ghost text-xs px-2 py-1"
                                                                            data-testid={`remove-video-rule-${v.id}`}
                                                                        >
                                                                            <Trash2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <button
                                                                        onClick={() =>
                                                                            setEditor({
                                                                                title: `Override for ${v.title}`,
                                                                                subtitle: "Different terms for this video only.",
                                                                                initial: null,
                                                                                onSave: (b) => saveVideo(v.id, b),
                                                                            })
                                                                        }
                                                                        className="btn-pill-ghost text-xs px-2 py-1 inline-flex items-center"
                                                                        data-testid={`add-video-rule-${v.id}`}
                                                                    >
                                                                        <Edit3 className="w-3.5 h-3.5 mr-1" /> Override
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {editor && (
                <RuleEditor
                    open
                    onClose={() => setEditor(null)}
                    title={editor.title}
                    subtitle={editor.subtitle}
                    initial={editor.initial}
                    onSave={editor.onSave}
                />
            )}
        </div>
    );
}
